import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { assistantApi } from '@/modules/assistant/services/assistant-api';
import { assistantQueryKeys } from '@/modules/assistant/hooks/query-keys';
import type { AssistantScope } from '@/modules/assistant/services/assistant-scope';

/**
 * One conversation's turns, oldest first.
 *
 * The API returns the turns in the order they belong — it allocates each turn's position
 * when it writes it, so the order is the order the turns were SAID, and it does not depend
 * on a timestamp that two turns in the same millisecond would share.
 *
 * So the console does not re-sort. It would be sorting by a timestamp the API has already
 * replaced with something better, and a tie in that timestamp would be broken arbitrarily —
 * which is how an answer ends up above the question it answered.
 */
export function useAssistantMessages(scope: AssistantScope, threadId: string | null) {
  return useQuery({
    queryKey: assistantQueryKeys.messages(scope, threadId ?? ''),
    queryFn: () => assistantApi.listMessages(scope, threadId ?? ''),
    enabled: threadId !== null && threadId.length > 0,
  });
}

/**
 * Sends one turn.
 *
 * The thread id travels with the message rather than being captured, so a
 * conversation that is switched mid-flight invalidates the conversation it was
 * actually sent to and not the one the panel happens to be showing.
 */
export function useSendAssistantMessage(scope: AssistantScope) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { threadId: string; message: string; route: string }) =>
      assistantApi.sendMessage(scope, input.threadId, {
        message: input.message,
        route: input.route,
      }),
    onSuccess: (_turn, input) => {
      void client.invalidateQueries({
        queryKey: assistantQueryKeys.messages(scope, input.threadId),
      });
      void client.invalidateQueries({ queryKey: assistantQueryKeys.threads(scope) });
    },
  });
}
